/**
 * Local copy of the backend scoring engine (backend/apps/inspections/domain/scoring.py), used for the live preview of a
 * draft while offline. The rules are data (TemplateVersion.rules); arithmetic is done on scaled integers so there is no
 * binary-float noise, and the result is tested against vectors exported from the Python engine.
 */
import type { CriterionResult, Level, OperatingState, RuleBand, Rules, ScoreThreshold } from '../api/types'

const SCORED = ['normal', 'medium', 'critical'] as const
const UNSCORED = ['not_applicable', 'not_measured', 'missing']
const SCALE = 1000

export interface ObservationInput {
  criterion: string
  level: Level | string
}

export interface LocalScore {
  assessment: 'inspected' | 'not_inspected'
  band: string
  raw_sum: string | null
  total: string | null
  rounding: { places: number; mode: string }
  scored_count: number
  criteria_count: number
  coverage: string
  operating_state: string
  breakdown: CriterionResult[]
  thresholds: ScoreThreshold[]
  warnings: string[]
  reference_only?: boolean
  label?: string
}

export function parseScaled(text: string): number {
  const clean = text.trim().replace(',', '.')
  if (!/^-?\d+(\.\d+)?$/.test(clean)) throw new Error(`Not a decimal number: ${text}`)
  const [whole, frac = ''] = clean.split('.')
  if (frac.length > 3) throw new Error(`Too many decimals: ${text}`)
  return Number(whole) * SCALE + Number(frac.padEnd(3, '0')) * (clean.startsWith('-') ? -1 : 1)
}

function format(scaled: number, decimals: number): string {
  const unit = 10 ** (3 - decimals)
  const rounded = Math.round(scaled / unit)
  const whole = Math.trunc(rounded / 10 ** decimals)
  const frac = String(Math.abs(rounded % 10 ** decimals)).padStart(decimals, '0')
  return decimals ? `${whole}.${frac}` : String(whole)
}

function decimalsOf(text: string): number {
  return (text.trim().replace(',', '.').split('.')[1] ?? '').length
}

function roundHalfUp(scaled: number, places: number): number {
  const unit = 10 ** (3 - places)
  return Math.floor((scaled + unit / 2) / unit) * unit
}

function bandFor(total: number, bands: RuleBand[]): RuleBand {
  for (const band of bands) {
    if (band.max === null || total <= parseScaled(band.max)) return band
  }
  throw new Error('No band matches')
}

interface Subset {
  rounding: Rules['rounding']
  criteria: Rules['criteria']
  bands: RuleBand[]
}

function score(rules: Subset, observations: ObservationInput[], operatingState: OperatingState | string): LocalScore {
  const byCode = new Map(rules.criteria.map((c) => [c.code, c]))
  const given = new Map<string, string>()
  for (const o of observations) {
    if (!byCode.has(o.criterion)) throw new Error(`Unknown criterion ${o.criterion}`)
    if (given.has(o.criterion)) throw new Error(`Duplicate observation for ${o.criterion}`)
    given.set(o.criterion, o.level)
  }

  const warnings: string[] = []
  const breakdown: CriterionResult[] = []
  let sum = 0
  let decimals = 1
  let scored = 0
  for (const c of rules.criteria) {
    const level = given.get(c.code) ?? 'missing'
    if ((SCORED as readonly string[]).includes(level)) {
      const text = c.values[level as (typeof SCORED)[number]]
      sum += parseScaled(text)
      decimals = Math.max(decimals, decimalsOf(text))
      scored += 1
      breakdown.push({
        criterion: c.code, number: c.number, name: c.name, level, status: 'scored',
        contribution: text, rule_text: c.texts[level as (typeof SCORED)[number]],
      })
    } else if (UNSCORED.includes(level)) {
      breakdown.push({
        criterion: c.code, number: c.number, name: c.name, level, status: 'not_assessed',
        contribution: null, rule_text: `Not assessed (${level}); excluded from the sum.`,
      })
    } else {
      warnings.push(`Criterion ${c.code} has an invalid or out-of-domain level '${level}'; excluded.`)
      breakdown.push({
        criterion: c.code, number: c.number, name: c.name, level, status: 'invalid',
        contribution: null, rule_text: `Invalid level '${level}'; excluded from the sum, needs review.`,
      })
    }
  }

  const n = rules.criteria.length
  const coverageText = (Math.floor((scored * 20000 + n) / (2 * n)) / 10000).toFixed(4)
  if (scored > 0 && scored < n) {
    warnings.push(`Partial coverage: ${scored} of ${n} criteria scored; the total understates the risk.`)
  }
  if (operatingState === 'off') warnings.push('Equipment was off (operating_state=off); only static criteria are meaningful.')

  const thresholds = rules.bands.map((b) => ({ ...b })) as ScoreThreshold[]
  const base = {
    rounding: { ...rules.rounding }, scored_count: scored, criteria_count: n, coverage: coverageText,
    operating_state: operatingState, breakdown, thresholds, warnings,
  }
  if (scored === 0) {
    return { ...base, assessment: 'not_inspected', band: 'not_assessed', raw_sum: null, total: null }
  }
  const places = rules.rounding.places
  const total = roundHalfUp(sum, places)
  return {
    ...base,
    assessment: 'inspected',
    band: bandFor(total, rules.bands).code,
    raw_sum: format(sum, decimals),
    total: format(total, places),
  }
}

export function scoreInspection(rules: Rules, observations: ObservationInput[], operatingState: OperatingState | string = 'unknown'): LocalScore {
  return score(rules, observations, operatingState)
}

/** The motor column: same criterion values, its own ranges; REFERENCE ONLY, never the official band. */
export function scoreMotorReference(
  rules: Rules,
  observations: ObservationInput[],
  operatingState: OperatingState | string = 'unknown',
): LocalScore | null {
  if (!rules.motor) return null
  const allowed = new Set(rules.motor.criteria)
  for (const o of observations) {
    if (!allowed.has(o.criterion)) throw new Error(`Criterion ${o.criterion} is not part of the motor reference diagnosis`)
  }
  const result = score(
    { rounding: rules.rounding, criteria: rules.criteria.filter((c) => allowed.has(c.code)), bands: rules.motor.bands },
    observations,
    operatingState,
  )
  return { ...result, reference_only: true, label: rules.motor.label || 'Reference' }
}
